import React, { useMemo } from "react";
import { Table, TournamentCard, Player } from "../../components/handball";
import AutoBracketWithResults from "../../components/handball/auto-bracket";

type Tournament = { id: string; name: string; date: string };
type Match = { id: string; tournamentId: string; round: number; player1Id: string; player2Id: string; winnerId: string; score?: string };

type Props = {
  players: Player[];
  tournaments: Tournament[];
  matches: Match[];
  participantsByTournament: Record<string, string[]>;
  topSeedNames: Record<string, string>;
  topSeeds: Record<string, number>;
  onOpen: (id: string) => void;
  openTournamentId: string | null;
  onClose: () => void;
};

function nextPowerOfTwo(n: number): number { let p = 1; while (p < n) p <<= 1; return p; }
function pickScore(seed: number): string { const options = ["21-19","21-18","21-17","21-16","21-15","21-14","21-12"]; return options[seed % options.length]; }

function seedingOrder(size: number): number[] { if (size===1) return [1]; if (size===2) return [1,2]; const half=size/2; const prev=seedingOrder(half); const mirror=prev.map(s=>size+1-s); const out:number[]=[]; for(let i=0;i<prev.length;i+=2) out.push(prev[i],mirror[i],mirror[i+1],prev[i+1]); return out; }

function simulateResults(ids: string[]): { round:number; pos:number; winner:1|2; score:string }[] {
  const size = nextPowerOfTwo(ids.length);
  const order = seedingOrder(size);
  const slotSeed = Array<number>(size).fill(Number.POSITIVE_INFINITY);
  for (let s=1;s<=ids.length;s++){ const slot=order.indexOf(s); slotSeed[slot]=s; }
  const out: { round:number; pos:number; winner:1|2; score:string }[] = [];
  let current = slotSeed.slice(); const rounds = Math.log2(size);
  for (let r=1;r<=rounds;r++){ const next:number[]=[]; const count=size>>r; for(let i=0;i<count;i++){ const a=current[2*i], b=current[2*i+1]; if(a===Infinity&&b===Infinity){ next.push(Infinity); continue;} if(a!==Infinity&&b===Infinity){ next.push(a); out.push({round:r,pos:i,winner:1,score:pickScore(i+r)}); continue;} if(a===Infinity&&b!==Infinity){ next.push(b); out.push({round:r,pos:i,winner:2,score:pickScore(i+r)}); continue;} const topWins=a<b; next.push(topWins?a:b); out.push({round:r,pos:i,winner:topWins?1:2,score:pickScore(i+r)});} current=next; }
  return out;
}

type Upcoming = Tournament & { count: number };

export default function Tournaments({ players, tournaments, matches, participantsByTournament, topSeedNames, topSeeds, onOpen, openTournamentId, onClose }: Props): JSX.Element {
  const sorted = useMemo(()=>[...tournaments].sort((a,b)=>b.date.localeCompare(a.date)),[tournaments]);
  const upcoming: Upcoming[] = useMemo(() => ([
    { id: "u1", name: "Prospect Park Fall Classic (18 players)", date: "2025-09-07", count: 18 },
    { id: "u2", name: "Astoria Courts Showdown (14 players)", date: "2025-09-21", count: 14 },
  ]), []);

  if (openTournamentId) {
    const t = tournaments.find(x=>x.id===openTournamentId);
    // Handle upcoming tournament view (no results, only first-level players seeded)
    const upcomingT = !t ? upcoming.find(x => x.id === openTournamentId) : undefined;
    if (upcomingT) {
      const bracketPlayers = players.slice(0, upcomingT.count).map(p => ({ id: p.id, name: p.name }));
      return (
        <div className="u-col u-gap-12">
          <div className="u-row" style={{ justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div className="card__header">{upcomingT.name}</div>
              <div className="card__meta">{new Date(upcomingT.date).toLocaleDateString()}</div>
            </div>
            <button className="btn btn--ghost" onClick={onClose}>Back</button>
          </div>
          <AutoBracketWithResults title={`${upcomingT.name} — ${new Date(upcomingT.date).toLocaleDateString()}`} players={bracketPlayers} results={[]} />
        </div>
      );
    }
    const tAssert = t!;
    const participantIds = participantsByTournament[openTournamentId] || [];
    const bracketPlayers = participantIds.map(pid => {
      const num = Number(pid.replace(/\D/g, ""));
      const name = topSeedNames[pid] ?? `Player ${String(num).padStart(2, "0")}`;
      return { id: pid, name };
    });
    const seedById: Record<string, number> = {};
    const presentTop = participantIds.filter(pid => topSeeds[pid] != null).sort((a,b)=>topSeeds[a]-topSeeds[b]);
    presentTop.forEach(pid => { seedById[pid] = topSeeds[pid]; });
    let s = Math.max(1, Math.max(0, ...Object.values(seedById)) + 1);
    participantIds.forEach(pid => { if (seedById[pid] == null) seedById[pid] = s++; });
    const rankedPlayers = [...bracketPlayers].sort((a,b)=> (seedById[a.id]||1e9) - (seedById[b.id]||1e9));
    const results = useMemo(()=>simulateResults(rankedPlayers.map(p=>p.id)),[openTournamentId]);

    return (
      <div className="u-col u-gap-12">
        <div className="u-row" style={{ justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div className="card__header">{t.name}</div>
            <div className="card__meta">{new Date(t.date).toLocaleDateString()}</div>
          </div>
          <button className="btn btn--ghost" onClick={onClose}>Back</button>
        </div>
        <AutoBracketWithResults title={`${t.name} — ${new Date(t.date).toLocaleDateString()}`} players={rankedPlayers} results={results} />
        <h4 className="card__header" style={{ fontSize: 18 }}>Players</h4>
        <Table>
          <thead>
            <tr><th>Player</th><th>Seed</th></tr>
          </thead>
          <tbody>
            {rankedPlayers.map(p => (
              <tr key={p.id}><td>{p.name}</td><td>{seedById[p.id]}</td></tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  }

  return (
    <div className="u-col u-gap-12">
      <div>
        <div className="card__header" style={{ marginBottom: 8 }}>Upcoming Tournaments</div>
        <div className="u-col u-gap-12">
          {upcoming.map(t => (
            <TournamentCard key={t.id} t={t} onOpen={onOpen} />
          ))}
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        <div className="card__header" style={{ marginBottom: 8 }}>Past Tournaments</div>
        <div className="u-col u-gap-12">
          {sorted.map(t => (
            <TournamentCard key={t.id} t={t} onOpen={onOpen} />
          ))}
        </div>
      </div>
    </div>
  );
}


